import classnames from 'classnames';
import React, {useState} from 'react';
import {Link} from 'react-router';
import type {InjectedRouter} from 'react-router';
import withRouter from 'react-router/lib/withRouter';

import {Icon, IconButton, Text} from '@albert-io/atomic';
import type {IconProp, IconStyle} from '@albert-io/atomic/atoms/Icon/Icon.react';
import {AssignmentMenuOptions} from './AssignmentMenuOptions';
import {history} from 'client/history';

import {
  formatDateRange,
  formatDateTime,
  formatEditedDate,
  getDueCopy
} from 'client/TeacherDashboard/rowDateCopy';
import type {StatusSegment} from './constants';
import type {TeacherAssignmentRowFieldsFragment} from './queries/TeacherAssignments.queries.generated';

// Margin-spaced bullet separator — more breathing room than a plain " • " string join.
const withBullets = (parts: string[]): React.ReactNode =>
  parts.map((part, index) => (
    <React.Fragment key={part}>
      {index > 0 && <span className='mx-x1'>•</span>}
      {part}
    </React.Fragment>
  ));

const getSubtitle = (
  assignment: TeacherAssignmentRowFieldsFragment,
  status: StatusSegment,
  showExactDueDate: boolean
): React.ReactNode => {
  if (status === 'draft') {
    return `Edited ${formatEditedDate(assignment.updatedAt)}`;
  }
  if (status === 'closed') {
    // Closed rows still show the due-date range across in-scope classes/students.
    const dueCopy = getDueCopy(assignment, showExactDueDate);
    return dueCopy ? withBullets(['Closed', dueCopy]) : 'Closed';
  }
  if (status === 'scheduled') {
    const {minStartDate, maxStartDate} = assignment;
    if (!minStartDate) {
      return 'Scheduled';
    }
    return `Scheduled for ${formatDateRange(minStartDate, maxStartDate, formatDateTime)}`;
  }
  // Open: "Due <date> • <completed> / <total> submissions". Counts are
  // (class, student) pairings, not distinct students.
  const parts: string[] = [];
  const dueCopy = getDueCopy(assignment, showExactDueDate);
  if (dueCopy) {
    parts.push(dueCopy);
  }
  const total = assignment.countOfCompletedStudents + assignment.countOfIncompleteStudents;
  if (total > 0) {
    parts.push(`${assignment.countOfCompletedStudents} / ${total} submissions`);
  }
  if (!parts.length) {
    return 'Open';
  }
  return withBullets(parts);
};

const STATUS_DISPLAY: Record<
  StatusSegment,
  {color: 'tertiary' | 'warning'; icon?: IconProp; iconStyle?: IconStyle}
> = {
  open: {color: 'tertiary'},
  scheduled: {color: 'warning', icon: 'clock', iconStyle: 'light'},
  closed: {color: 'tertiary', icon: 'ban', iconStyle: 'regular'},
  draft: {color: 'tertiary'}
};

interface Props {
  /** The acting (possibly masqueraded) teacher — scopes reports and gates delete. */
  actorTeacherId: string;
  assignment: TeacherAssignmentRowFieldsFragment;
  /** Adds the classroom dimension to the report link (class-scoped list). */
  classroomId?: string;
  /** Grade/release affordance for FRQ assignments; the list owns whether to render it (or none). */
  gradeCell?: React.ReactNode;
  /** Passed down to the menu, which gates cross-class "View Student Answers" on it. */
  isFrqGradingEnabled?: boolean;
  /** Leading LMS grade-sync affordance; the list owns whether/which one to render (or none). */
  lmsCell?: React.ReactNode;
  onDeleteComplete: () => void;
  router: InjectedRouter;
  /** The list segment the row was fetched under. */
  status: StatusSegment;
}

const AssignmentRowBase = ({
  actorTeacherId,
  assignment,
  classroomId,
  gradeCell,
  isFrqGradingEnabled,
  lmsCell,
  onDeleteComplete,
  router,
  status
}: Props) => {
  const [showExactDueDate, setShowExactDueDate] = useState(false);

  const isDraft = status === 'draft';
  const editPath = `/assignment-builder/create-assignment/${assignment.id}/settings${
    isDraft ? '/draft' : ''
  }`;
  const reportPath = actorTeacherId
    ? `/reports/teachers/${actorTeacherId}${
        classroomId ? `/classrooms/${classroomId}` : ''
      }/assignments/${assignment.id}/students`
    : null;
  const isCreator = Boolean(actorTeacherId) && assignment.teacherId === actorTeacherId;
  // Class-scoped delete only unassigns this class (open to its teachers); cross-class
  // delete removes the assignment entirely, so it's creator-only.
  const canEdit = (Boolean(classroomId) && !isDraft) || isCreator;

  const subtitle = getSubtitle(assignment, status, showExactDueDate);
  const {color: statusColor, icon: statusIcon, iconStyle: statusIconStyle} = STATUS_DISPLAY[status];

  const goToBuilder = () => history.pushState(null, editPath);

  return (
    // Clicking anywhere on the row opens the builder. The name stays a real <Link> so keyboard/AT users
    // and middle-click/open-in-new-tab still work — the row onClick is a mouse-only convenience on top of
    // it (hence the a11y disable). The action cluster stops propagation so its buttons don't also navigate.
    // oxlint-disable-next-line jsx-a11y/click-events-have-key-events
    <div
      className='gap-x2 rounded-x1 p-x1 hover:background-primary-hover active:background-primary-pressed flex cursor-pointer items-center'
      onClick={goToBuilder}
      onMouseEnter={() => setShowExactDueDate(true)}
      onMouseLeave={() => setShowExactDueDate(false)}
      onFocus={() => setShowExactDueDate(true)}
      onBlur={() => setShowExactDueDate(false)}
    >
      <div
        className={classnames(
          'flex h-x5 w-x5 shrink-0 items-center justify-center rounded-full',
          isDraft ? 'border border-dashed border-border-bold' : 'background-brand'
        )}
      >
        <Icon
          className='text-m'
          color={isDraft ? 'tertiary' : 'primary-inverse-static'}
          icon='file-lines'
          iconStyle='regular'
        />
      </div>
      <div className='min-w-x0 flex grow flex-col'>
        <Link className='text-m content-primary block truncate no-underline' to={editPath}>
          {assignment.name}
        </Link>
        {subtitle ? (
          <Text as='div' className='truncate' color={statusColor} size='s'>
            {statusIcon ? (
              <Icon
                className='mr-x0-half text-xs!'
                color={statusColor}
                icon={statusIcon}
                iconStyle={statusIconStyle}
              />
            ) : null}
            {subtitle}
          </Text>
        ) : null}
      </div>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
      <div className='gap-x0-half flex shrink-0 items-center' onClick={(e) => e.stopPropagation()}>
        {gradeCell}
        {lmsCell}
        {!isDraft && reportPath && (
          <IconButton
            className='relative'
            icon='chart-line'
            iconStyle='regular'
            label='View report'
            tooltip='View report'
            onClick={() => history.pushState(null, reportPath)}
            size='s'
          />
        )}
        <IconButton
          className='relative'
          icon='pencil'
          iconStyle='regular'
          label='Edit'
          tooltip='Edit'
          onClick={() => history.pushState(null, editPath)}
          size='s'
        />
        <span className='relative inline-flex'>
          <AssignmentMenuOptions
            assignmentId={assignment.id}
            assignmentName={assignment.name}
            isDraft={isDraft}
            canEdit={canEdit}
            classroomId={classroomId}
            isFrqGradingEnabled={isFrqGradingEnabled}
            reportPath={reportPath}
            updatedAt={assignment.updatedAt}
            onDeleteComplete={onDeleteComplete}
            router={router}
          />
        </span>
      </div>
    </div>
  );
};

// Memoized so the row bails when the list re-renders (a keystroke, or a global store 'change') with its
// props unchanged. Wrapping outside withRouter is safe: withRouter subscribes to the router itself, so
// navigation still re-renders the row despite the memo.
export const AssignmentRow = React.memo(withRouter(AssignmentRowBase));
